/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import * as Actions from 'payment-calculator-v25/actions/paymentCalculatorActions.js';
import 'payment-calculator-v25/components/PaymentCalculatorDetails/components/FinanceMethodPage/FinanceMethodPage.scss';

import { PurchaseMethodsV25, Vehicle, PaymentOptionsMetadata, Quote } from '@sm360/widget-commons-vehicle';
import { LocaleService } from '@sm360/widget-commons-core-services';
import CustomizePayment from 'payment-calculator-v25/components/PaymentCalculatorDetails/components/CustomizePayment/CustomizePayment.jsx';
import CalculatorCashdown from 'payment-calculator-v25/components/PaymentCalculatorDetails/components/CalculatorCashdown/CalculatorCashdown.jsx';
import CalculatorTerms from 'payment-calculator-v25/components/PaymentCalculatorDetails/components/CalculatorTerms/CalculatorTerms.jsx';
import CalculatorTradein from 'payment-calculator-v25/components/PaymentCalculatorDetails/components/CalculatorTradein/CalculatorTradein.jsx';
import Distance from 'payment-calculator-v25/components/Distance/Distance.jsx';
import CalculatorRebateStandardRate from 'payment-calculator-v25/components/PaymentCalculatorDetails/components/CalculatorRebateStandardRate/CalculatorRebateStandardRate.jsx';
import ResidualValue from 'payment-calculator-v25/components/ResidualValue/ResidualValue.jsx';

const FinanceMethodPage = ({
    purchaseMethods,
    tradeInVehicle,
    actions,
    paymentOptionsMetadata,
    quote,
    config,
    displayCreditRate,
}) => {
    const prices = purchaseMethods.getSelectedPurchaseMethodPaymentOption();
    const leaseOptions = purchaseMethods.isLease() && purchaseMethods.getCalculatorOptions();
    let kmPlanBlock;

    if (leaseOptions) {
        kmPlanBlock = (
            <div>
                <Distance
                    purchaseMethods={purchaseMethods}
                    onKMPlanClick={actions.setKMPlan}
                    paymentOptionsMetadata={paymentOptionsMetadata}
                    quote={quote}
                    config={config}
                />
                <ResidualValue residualValue={leaseOptions?.buyBackAmount} />
            </div>
        );
    }

    const { tradeIn, lien } = prices || {};

    return (
        <div className="calculatorRow">
            <div className="calculatorColumn">
                <CustomizePayment
                    purchaseMethods={purchaseMethods}
                    updateTargetPayment={actions.updateTargetPayment}
                    selectPaymentFrequency={actions.selectPaymentFrequency}
                    setCashDown={actions.setCashDown}
                    paymentOptionsMetadata={paymentOptionsMetadata}
                    quote={quote}
                    config={config}
                />
                <CalculatorCashdown
                    setCashDown={actions.setCashDown}
                    purchaseMethods={purchaseMethods}
                    quote={quote}
                    config={config}
                />
                {kmPlanBlock}
            </div>
            <div className="calculatorColumn">
                <CalculatorTerms
                    purchaseMethods={purchaseMethods}
                    config={config}
                    displayCreditRate={displayCreditRate}
                />
                <div className="calculatorColumn__disclaimer">
                    {LocaleService.t('PAYMENT_CALCULATOR_V25.DISCLAIMER')}
                </div>
            </div>
            <div className="calculatorColumn">
                <CalculatorTradein
                    tradeInVehicle={tradeInVehicle}
                    tradeIn={tradeIn}
                    lien={lien}
                    setTradeIn={actions.setTradeIn}
                    setLien={actions.setLien}
                    setTradeInAndLien={actions.setTradeInAndLien}
                    purchaseMethods={purchaseMethods}
                    quote={quote}
                    config={config}
                    setShiftEvent={actions.setShiftEvent}
                />
                {/* Not provided by the backend V2.5  (DATA-4959)
                <CalculatorRebateStandardRate stdRebate={purchaseMethods.getSelectedRebatesAtStandardRate()} /> */}
            </div>
        </div>
    );
};

FinanceMethodPage.propTypes = {
    purchaseMethods: PropTypes.instanceOf(PurchaseMethodsV25.PurchaseMethodsModelV25).isRequired,
    paymentOptionsMetadata: PropTypes.arrayOf(PropTypes.instanceOf(PaymentOptionsMetadata.PaymentOptionsMetadataModel))
        .isRequired,
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModelV25).isRequired,
    actions: PropTypes.shape({
        updateTargetPayment: PropTypes.func.isRequired,
        selectPaymentFrequency: PropTypes.func.isRequired,
        setCashDown: PropTypes.func.isRequired,
        setTradeIn: PropTypes.func.isRequired,
        setLien: PropTypes.func.isRequired,
        setTradeInAndLien: PropTypes.func.isRequired,
        setKMPlan: PropTypes.func.isRequired,
    }),
    displayCreditRate: PropTypes.bool,
};

FinanceMethodPage.defaultProps = {
    actions: [],
    displayCreditRate: false,
};

FinanceMethodPage.displayName = 'FinanceMethodPage';

function mapStateToProps(state) {
    return {
        purchaseMethods: state.purchaseMethodsV25,
        tradeInVehicle: state.tradeInVehicle,
        paymentOptionsMetadata: state.paymentOptionsMetadata,
        quote: state.quoteV25,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Actions, dispatch),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FinanceMethodPage);
